import React, { forwardRef, useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import Flatpickr from 'react-flatpickr';
import { BaseOptions, Hook } from 'flatpickr/dist/types/options';
import { Instance } from 'flatpickr/dist/types/instance';
import { store } from 'Models/Store';
import ErrorMessage from './ErrorMessage';
import { DISABLED_INPUT } from './inputTheme';
import 'flatpickr/dist/themes/material_green.css';

interface FlatPickrDateInputProps {
	label?: string;
	error?: string;
	required?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	placeholder?: string;
	className?: string;
	id?: string;
	value?: string;
	min?: string;
	max?: string;
	onChange?: (date: string) => void;
	onClear?: () => void;
	/** Additional flatpickr options */
	flatpickrOptions?: Partial<BaseOptions>;
	/** Enable time selection */
	enableTime?: boolean;
	/** Date format for display (flatpickr altFormat) */
	displayFormat?: string;
	/** Date format for value (flatpickr dateFormat) */
	valueFormat?: string;
	/**
	 * Something shown inside the field, against its left edge — an icon naming what the field takes.
	 * The input is padded to clear it and it does not take clicks, so the whole box still opens the
	 * picker.
	 */
	startAdornment?: React.ReactNode;
}

export const FlatPickrDateInput = forwardRef<HTMLInputElement, FlatPickrDateInputProps>(
	(
		{
			label,
			error,
			required,
			disabled = false,
			readOnly = false,
			placeholder,
			className,
			id,
			value,
			min,
			max,
			onChange,
			onClear,
			flatpickrOptions,
			enableTime = false,
			displayFormat = 'j F Y',
			valueFormat = 'Y-m-d',
			startAdornment,
		},
		ref,
	) => {
		const inputId = id || label?.replace(/\s+/g, '-').toLowerCase();
		const flatpickrRef = useRef<Flatpickr | null>(null);

		const handleChange = useCallback(
			(dates: Date[]) => {
				if (dates.length > 0 && onChange) {
					// Format as YYYY-MM-DD for consistency
					const d = dates[0];
					const year = d.getFullYear();
					const month = String(d.getMonth() + 1).padStart(2, '0');
					const day = String(d.getDate()).padStart(2, '0');
					const date = `${year}-${month}-${day}`;

					if (!enableTime) {
						onChange(date);
						return;
					}

					// The time comes back too when the picker offers one — a date-only answer would
					// drop the half of the choice the caller turned the clock on for.
					const hours = String(d.getHours()).padStart(2, '0');
					const minutes = String(d.getMinutes()).padStart(2, '0');

					onChange(`${date} ${hours}:${minutes}`);
				}
			},
			[onChange, enableTime],
		);

		const handleClear = useCallback(() => {
			if (flatpickrRef.current) {
				(flatpickrRef.current as any).flatpickr?.clear();
			}
			onClear?.();
		}, [onClear]);

		/**
		 * What the visible input is, in the end: flatpickr replaces the real input with an "alt" one
		 * of its own and leaves the real one hidden, so everything the class list says has to be
		 * copied across to it by hand.
		 *
		 * The two inline styles are the ones a class can't win: flatpickr marks the alt input
		 * readonly, and the global `input[readonly]` rule in `inputs.scss` greys anything carrying
		 * that attribute. So an enabled field is painted back to white here — and a disabled one to
		 * the same fill the `New` text inputs use, rather than being left looking editable, which is
		 * what the unconditional white did.
		 */
		const syncAltInput = useCallback(
			(altInput: HTMLInputElement, sourceClassName: string) => {
				// eslint-disable-next-line no-param-reassign
				altInput.className = sourceClassName;
				// eslint-disable-next-line no-param-reassign
				altInput.disabled = disabled;
				// eslint-disable-next-line no-param-reassign
				altInput.style.backgroundColor = disabled ? DISABLED_INPUT.background : '#ffffff';
				// eslint-disable-next-line no-param-reassign
				altInput.style.cursor = disabled ? 'not-allowed' : 'pointer';
			},
			[disabled],
		);

		/*
		 * The fill and the cursor are set in every branch rather than once at the top. Held in common
		 * they were `bg-white cursor-pointer`, which a disabled field then had to beat with
		 * `bg-gray-100 cursor-not-allowed` — and between two utilities of the same weight it is the
		 * stylesheet's order that decides, not the order they are written in here.
		 */
		const inputClassName = classNames(
			'px-3 py-3 border rounded-md outline-none transition-all w-full',
			'placeholder:text-gray-400 text-sm',
			// eslint-disable-next-line no-nested-ternary
			disabled
				? 'border-gray-300 bg-gray-100 cursor-not-allowed text-gray-500 placeholder:text-gray-300'
				: error
					? 'border-red-400 bg-red-50 cursor-pointer text-red-900 focus:border-red-400 focus:ring-1 focus:ring-[#ef4444]'
					: 'border-gray-300 bg-white cursor-pointer hover:border-gray-400 focus:border-[var(--colour-brand-primary)] focus:ring-1 focus:ring-[var(--colour-brand-primary)]',
			readOnly && 'bg-gray-50 text-gray-600',
			startAdornment && 'pl-8',
			className,
		);

		/*
		 * The copy above happens once, when the picker is built. Anything that changes the class list
		 * afterwards — the field going disabled, an error arriving — would otherwise never reach the
		 * input that is actually on screen.
		 */
		useEffect(() => {
			const { altInput } = ((flatpickrRef.current as any)?.flatpickr ?? {}) as { altInput?: HTMLInputElement };

			if (altInput) {
				syncAltInput(altInput, inputClassName);
			}
		}, [inputClassName, syncAltInput]);

		/**
		 * flatpickr only reads a typed hour or minute when that box loses focus by tabbing, so a time typed and
		 * then left by clicking away closed the picker on the old value. On close, whatever the boxes hold is
		 * taken as the time. A calendar with nothing picked yet is left alone — there is no day to put it on.
		 */
		const commitTypedTime = useCallback(
			(_dates: Date[], _text: string, instance: Instance) => {
				const { hourElement, minuteElement, amPM } = instance;
				if (!enableTime || !hourElement || !minuteElement || hourElement.value === '' || minuteElement.value === '') {
					return;
				}
				const typedHour = Number(hourElement.value);
				const typedMinute = Number(minuteElement.value);
				if (!Number.isInteger(typedHour) || !Number.isInteger(typedMinute)) {
					return;
				}
				const base = instance.selectedDates[0];
				if (!base && !instance.config.noCalendar) {
					return;
				}
				const hour = amPM ? (typedHour % 12) + (amPM.textContent === 'PM' ? 12 : 0) : typedHour;
				const next = new Date(base ?? instance.now);
				next.setHours(Math.min(Math.max(hour, 0), 23), Math.min(Math.max(typedMinute, 0), 59), 0, 0);
				if (base?.getTime() !== next.getTime()) {
					instance.setDate(next, true);
				}
			},
			[enableTime],
		);

		const callerOnClose = flatpickrOptions?.onClose;
		const onCloseHooks = [commitTypedTime, ...(callerOnClose ? ([] as Hook[]).concat(callerOnClose) : [])];

		// What comes out carries a time when the picker offers one, so what flatpickr reads back in
		// has to expect one. Only the default moves — a caller naming its own format keeps it.
		const resolvedValueFormat = enableTime && valueFormat === 'Y-m-d' ? 'Y-m-d H:i' : valueFormat;

		const options: Partial<BaseOptions> = {
			altInput: true,
			altFormat: displayFormat,
			dateFormat: resolvedValueFormat,
			enableTime,
			allowInput: false,
			minDate: min,
			maxDate: max,
			disableMobile: true,
			onReady: (_dates, _str, instance) => {
				// Copy Tailwind classes from the hidden input to the alt input
				if (instance.altInput && instance.input) {
					syncAltInput(instance.altInput, instance.input.className);
					// Set placeholder on alt input since flatpickr doesn't forward it
					if (placeholder) {
						instance.altInput.placeholder = placeholder;
					}
				}
				// Add app location class so the calendar picks up the existing theme styles
				instance.calendarContainer?.classList?.add(store.appLocation);
			},
			...flatpickrOptions,
			// After the caller's options, so a caller's own `onClose` runs alongside this rather than replacing it.
			onClose: onCloseHooks,
		};

		return (
			<div className="flex flex-col gap-1 w-full">
				{label && (
					<label htmlFor={inputId} className="text-sm text-gray-800">
						{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
					</label>
				)}
				<div className="flex relative items-center custom-input flatpickr-new-input">
					{startAdornment && (
						<div className="flex absolute left-3 items-center text-gray-500 pointer-events-none">
							{startAdornment}
						</div>
					)}
					<Flatpickr
						ref={flatpickrRef}
						id={inputId}
						value={value}
						disabled={disabled}
						placeholder={placeholder}
						options={options}
						className={inputClassName}
						onChange={handleChange}
					/>
					{onClear && !readOnly && !disabled && value && (
						<button
							type="button"
							className="absolute right-3 text-gray-400 hover:text-gray-600 z-10"
							onClick={handleClear}
						>
							<span className="material-symbols-outlined text-base">close</span>
						</button>
					)}
				</div>
				<ErrorMessage error={error} />
			</div>
		);
	},
);
